import {
  billingSuggestionEnvelopeSchema,
  brightFuturesEnvelopeSchema,
  createDevelopmentalScreeningRequestSchema,
  createGrowthMeasurementRequestSchema,
  developmentalScreeningEnvelopeSchema,
  developmentalScreeningListEnvelopeSchema,
  growthMeasurementEnvelopeSchema,
  growthMeasurementListEnvelopeSchema,
  immunizationGapEnvelopeSchema,
  type BillingSuggestionEnvelope,
  type BrightFuturesEnvelope,
  type CreateDevelopmentalScreeningRequest,
  type CreateGrowthMeasurementRequest,
  type DevelopmentalScreeningResponse,
  type GrowthMeasurementResponse,
  type ImmunizationGapEnvelope,
} from '@emr/shared';

import { apiRequest } from './client.ts';

/**
 * The pediatrics specialty-module API surface (`BACKLOG.md` M8), validated against `@emr/shared`'s
 * own request/response schemas — the exact same schemas `server/src/pediatrics/routes.ts` validates
 * request bodies with, matching `api/dermatology.ts`'s own stated reasoning.
 */

export type {
  BillingSuggestionEnvelope,
  BrightFuturesEnvelope,
  CreateDevelopmentalScreeningRequest,
  CreateGrowthMeasurementRequest,
  DevelopmentalScreeningResponse,
  GrowthMeasurementResponse,
  ImmunizationGapEnvelope,
};

function peds(patientId: string, suffix = ''): string {
  return `/api/patients/${patientId}/pediatrics${suffix}`;
}

export async function fetchGrowthMeasurements(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly GrowthMeasurementResponse[]> {
  const response = await apiRequest(
    peds(patientId, '/growth-measurements'),
    growthMeasurementListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.measurements;
}

export async function createGrowthMeasurement(
  patientId: string,
  input: CreateGrowthMeasurementRequest,
): Promise<GrowthMeasurementResponse> {
  createGrowthMeasurementRequestSchema.parse(input);
  const response = await apiRequest(
    peds(patientId, '/growth-measurements'),
    growthMeasurementEnvelopeSchema,
    { method: 'POST', body: input },
  );
  return response.measurement;
}

export async function fetchDevelopmentalScreenings(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly DevelopmentalScreeningResponse[]> {
  const response = await apiRequest(
    peds(patientId, '/developmental-screenings'),
    developmentalScreeningListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.screenings;
}

export async function createDevelopmentalScreening(
  patientId: string,
  input: CreateDevelopmentalScreeningRequest,
): Promise<DevelopmentalScreeningResponse> {
  createDevelopmentalScreeningRequestSchema.parse(input);
  const response = await apiRequest(
    peds(patientId, '/developmental-screenings'),
    developmentalScreeningEnvelopeSchema,
    { method: 'POST', body: input },
  );
  return response.screening;
}

export async function fetchImmunizationGaps(
  patientId: string,
  signal?: AbortSignal,
): Promise<ImmunizationGapEnvelope> {
  return apiRequest(
    peds(patientId, '/immunization-gaps'),
    immunizationGapEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
}

export async function fetchBrightFuturesSchedule(
  patientId: string,
  signal?: AbortSignal,
): Promise<BrightFuturesEnvelope> {
  return apiRequest(
    peds(patientId, '/bright-futures'),
    brightFuturesEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
}

export async function fetchBillingSuggestions(
  patientId: string,
  query: {
    readonly newPatient?: boolean | undefined;
    readonly vaccineComponentCount?: number | undefined;
  },
  signal?: AbortSignal,
): Promise<BillingSuggestionEnvelope> {
  const params = new URLSearchParams();
  if (query.newPatient !== undefined) params.set('newPatient', String(query.newPatient));
  if (query.vaccineComponentCount !== undefined) {
    params.set('vaccineComponentCount', String(query.vaccineComponentCount));
  }
  const qs = params.toString();
  return apiRequest(
    peds(patientId, `/billing-suggestions${qs === '' ? '' : `?${qs}`}`),
    billingSuggestionEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
}
